import { useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm, message,
  Tabs, Switch, ColorPicker, Divider, List, Card
} from "antd";
import { Plus, Settings, GitBranch, CheckSquare, FileText, Trash2, GripVertical } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import MyPagination from "../../components/Pagination";
import { SEMANTIC_HEX } from "../../../constants/statusColors";
import {
  useJobTypeList, useJobTypeCreate, useJobTypeUpdate, useJobTypeDelete,
  usePipelineList, usePipelineCreate, usePipelineUpdate, usePipelineDelete,
  useStageCreate, useStageUpdate, useStageDelete,
  useChecklistList, useChecklistDetail, useChecklistCreate, useChecklistUpdate, useChecklistDelete, useSaveChecklistItems,
  useFormTemplateList, useFormTemplateDetail, useFormTemplateCreate, useFormTemplateUpdate, useFormTemplateDelete,
  useCertificationTypeList, useRequiredCertificationList, useSaveRequiredCertifications,
} from "../../../hooks/api/job-config/hooks";
import {
  IJobType, IJobTypeCreatePayload, IJobTypeUpdatePayload,
  IJobStatusPipeline, IJobStatusPipelineCreatePayload,
  IJobStatusStageCreatePayload,
  IChecklistTemplate, IChecklistCreatePayload, IChecklistUpdatePayload, IChecklistItem,
  IFormTemplate, IFormTemplateCreatePayload, IFormTemplateUpdatePayload,
} from "../../../types/api/job-config";

const DEFAULT_NUQS_CONFIG = { throttleMs: Infinity };

// ── Required Certifications — compliance tags shown in the Job Types table.
// Each row fetches its own required-cert list via the already-built
// GET /job-config/job-types/:id/required-certifications endpoint — job type
// counts per company are small, so a per-row query is simpler than adding a
// second "list all, grouped by job type" endpoint just for this table.
function RequiredCertsTags({ jobTypeId }: { jobTypeId: number }) {
  const { data } = useRequiredCertificationList(jobTypeId);
  const certs = data ?? [];
  if (!certs.length) return <span className="text-slate-400 text-xs">None</span>;
  return (
    <div className="flex gap-1 flex-wrap">
      {certs.map((c) => (
        <Tag key={c.id} color="red" className="text-xs">{c.certification_name}</Tag>
      ))}
    </div>
  );
}

// ── Job Types Tab ──────────────────────────────────────────────────────────
function JobTypesTab() {
  const [editing, setEditing] = useState<IJobType | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useJobTypeList({ page: 1, limit: 20 });
  const items: IJobType[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const { data: certTypes } = useCertificationTypeList();
  const certTypeOptions = (certTypes ?? []).map((ct) => ({ value: ct.name, label: ct.issuing_body ? `${ct.name} (${ct.issuing_body})` : ct.name }));
  const { data: requiredCertsForEditing } = useRequiredCertificationList(modalOpen ? editing?.id ?? null : null);
  const createMut = useJobTypeCreate({ onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const updateMut = useJobTypeUpdate({ onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const deleteMut = useJobTypeDelete({ onSuccess: () => { msgApi.success("Job type deleted"); refetchWithQuery({ page: 1 }); } });
  const saveCertsMut = useSaveRequiredCertifications({ onError: (e) => msgApi.error(e?.response?.data?.message || "Error saving required certifications") });

  // Pre-populate the multi-select with the job type's current required cert
  // names once they've loaded (fires again whenever the edit modal re-opens
  // for a different row, since the query key includes editing?.id).
  useEffect(() => {
    if (modalOpen && editing && requiredCertsForEditing) {
      form.setFieldValue("required_certification_names", requiredCertsForEditing.map((c) => c.certification_name));
    }
  }, [modalOpen, editing, requiredCertsForEditing, form]);

  const onFinish = (vals: any) => {
    const { required_certification_names, ...jobTypeVals } = vals;
    const payload = { ...jobTypeVals, colour: typeof jobTypeVals.colour === "object" ? jobTypeVals.colour.toHexString() : jobTypeVals.colour };
    const afterSave = (jobTypeId: number) => {
      saveCertsMut.mutate(
        { jobTypeId, certification_names: required_certification_names ?? [] },
        { onSuccess: () => { msgApi.success(editing ? "Job type updated" : "Job type created"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); } },
      );
    };
    if (editing) {
      updateMut.mutate({ ...payload, id: editing.id } as IJobTypeUpdatePayload, { onSuccess: () => afterSave(editing.id) });
    } else {
      createMut.mutate(payload as IJobTypeCreatePayload, { onSuccess: (res) => afterSave((res as any).data.id) });
    }
  };

  const columns: ColumnType<IJobType>[] = [
    {
      title: "Name", dataIndex: "name", key: "name",
      render: (v, r) => (
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: r.colour || "var(--color-primary)" }} />
          <span className="font-medium text-slate-800">{v}</span>
        </div>
      )
    },
    { title: "Slug", dataIndex: "slug", key: "slug", render: (v) => <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v}</span> },
    { title: "Duration", dataIndex: "default_duration_mins", key: "default_duration_mins", render: (v) => `${v} mins` },
    { title: "Requires", key: "requires", render: (_, r) => (
      <div className="flex gap-1 flex-wrap">
        {r.requires_parts && <Tag color="blue" className="text-xs">Parts</Tag>}
        {r.requires_cert && <Tag color="purple" className="text-xs">Cert</Tag>}
        {r.requires_photo && <Tag color="orange" className="text-xs">Photo</Tag>}
        {r.requires_signature && <Tag color="geekblue" className="text-xs">Signature</Tag>}
        {r.require_before_photo && <Tag color="gold" className="text-xs">Before Photo</Tag>}
        {r.require_after_photo && <Tag color="volcano" className="text-xs">After Photo</Tag>}
      </div>
    )},
    { title: "Required Certs", key: "required_certs", render: (_, r) => <RequiredCertsTags jobTypeId={r.id} /> },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete job type?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Job Types ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }} className="flex items-center gap-2">Add Job Type</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <Modal title={editing ? "Edit Job Type" : "Add Job Type"} open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={540}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Electrical Installation" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="colour" label="Colour" initialValue="#013BBE">
              <ColorPicker format="hex" />
            </Form.Item>
            <Form.Item name="default_duration_mins" label="Default Duration (mins)" initialValue={60}>
              <Input type="number" min={15} step={15} />
            </Form.Item>
          </div>
          <Divider className="my-3" />
          <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wide">Requirements</p>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="requires_parts" label="Requires Parts" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item name="requires_cert" label="Requires Certification" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item name="requires_photo" label="Requires Photo" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item name="requires_signature" label="Requires Signature" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item name="require_before_photo" label="Requires Before Photo" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item name="require_after_photo" label="Requires After Photo" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
          </div>
          <Divider className="my-3" />
          <p className="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wide">Compliance</p>
          <Form.Item
            name="required_certification_names"
            label="Required Certifications"
            initialValue={[]}
            extra="Workers without a valid (active, non-expired) certification of every type selected here cannot be assigned to jobs of this type. Create new certification types via HR & Compliance → Certification Types first."
          >
            <Select mode="multiple" options={certTypeOptions} placeholder="Select required certifications…" allowClear />
          </Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending || saveCertsMut.isPending}>{editing ? "Save Changes" : "Create"}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Pipelines Tab ──────────────────────────────────────────────────────────
function PipelinesTab() {
  const [stageModal, setStageModal] = useState<{ open: boolean; pipeline_id: number | null }>({ open: false, pipeline_id: null });
  const [pipelineModal, setPipelineModal] = useState(false);
  const [form] = Form.useForm();
  const [stageForm] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: pipelinesData, isLoading, refetch } = usePipelineList({});
  const pipelines: IJobStatusPipeline[] = (pipelinesData as any)?.data ?? [];
  const { data: jobTypesData } = useJobTypeList({ page: 1, limit: 100 });
  const jobTypes: IJobType[] = (jobTypesData as any)?.data ?? [];
  const createPipeline = usePipelineCreate({ onSuccess: () => { msgApi.success("Pipeline created"); setPipelineModal(false); form.resetFields(); refetch(); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const deletePipeline = usePipelineDelete({ onSuccess: () => { msgApi.success("Pipeline deleted"); refetch(); } });
  const createStage = useStageCreate({ onSuccess: () => { msgApi.success("Stage added"); setStageModal({ open: false, pipeline_id: null }); stageForm.resetFields(); refetch(); } });
  const deleteStage = useStageDelete({
    onSuccess: () => { msgApi.success("Stage deleted"); refetch(); },
    // The axios interceptor normalises every failed request down to
    // {message, ...} (status code and `extra.job_count` don't survive it),
    // but the backend's warning text is preserved on `message` — surface it
    // in a confirm dialog with the real consequence spelled out, instead of
    // the previous behaviour of the error going nowhere at all.
    onError: (e: any, variables: { id: number; force?: boolean }) => {
      Modal.confirm({
        title: "Couldn't delete stage",
        content: e?.message || "This stage could not be deleted.",
        okText: "Delete anyway",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: () => deleteStage.mutate({ id: variables.id, force: true }),
      });
    },
  });

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Status Pipelines ({pipelines.length})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => setPipelineModal(true)} className="flex items-center gap-2">Add Pipeline</ButtonWrapper>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : pipelines.map((p) => (
        <Card key={p.id} className="mb-4 border-slate-200" size="small"
          title={
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{p.name}</span>
                {p.is_default && <Tag color="blue" className="text-xs">Default</Tag>}
                {p.job_type_id && <Tag color="purple" className="text-xs">{jobTypes.find((j) => j.id === p.job_type_id)?.name || `Type #${p.job_type_id}`}</Tag>}
              </div>
              <Space>
                <Button size="small" icon={<Plus size={12} />} onClick={() => { setStageModal({ open: true, pipeline_id: p.id }); }}>Add Stage</Button>
                <Popconfirm title="Delete pipeline?" onConfirm={() => deletePipeline.mutate(p.id)}>
                  <Button size="small" danger icon={<Trash2 size={12} />} />
                </Popconfirm>
              </Space>
            </div>
          }
        >
          <div className="flex flex-wrap gap-2 py-1">
            {(p.stages || []).length === 0 ? (
              <span className="text-slate-400 text-sm">No stages yet — add one</span>
            ) : (
              (p.stages || []).map((s, idx) => (
                <div key={s.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.colour || SEMANTIC_HEX.neutral }} />
                  <span className="text-sm font-medium text-slate-700">{s.name}</span>
                  {s.is_start && <Tag color="green" className="text-xs !m-0">Start</Tag>}
                  {s.is_complete && <Tag color="blue" className="text-xs !m-0">Complete</Tag>}
                  {s.is_cancelled && <Tag color="red" className="text-xs !m-0">Cancel</Tag>}
                  {s.triggers_invoice && <Tag color="gold" className="text-xs !m-0">Invoice</Tag>}
                  <Popconfirm title="Delete stage?" onConfirm={() => deleteStage.mutate({ id: s.id })}>
                    <button className="text-slate-400 hover:text-red-500 ml-1"><Trash2 size={11} /></button>
                  </Popconfirm>
                </div>
              ))
            )}
          </div>
        </Card>
      ))}

      <Modal title="Add Pipeline" open={pipelineModal} onCancel={() => { setPipelineModal(false); form.resetFields(); }} footer={null} width={480}>
        <Form form={form} layout="vertical" onFinish={(vals) => createPipeline.mutate(vals as IJobStatusPipelineCreatePayload)} className="mt-4">
          <Form.Item name="name" label="Pipeline Name" rules={[{ required: true }]}><Input placeholder="e.g. Standard Job Flow" /></Form.Item>
          <Form.Item name="job_type_id" label="Job Type (optional — leave blank for all types)">
            <Select allowClear options={jobTypes.map((j) => ({ value: j.id, label: j.name }))} />
          </Form.Item>
          <Form.Item name="is_default" label="Set as default pipeline" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setPipelineModal(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createPipeline.isPending}>Create</Button>
          </div>
        </Form>
      </Modal>

      <Modal title="Add Stage" open={stageModal.open} onCancel={() => { setStageModal({ open: false, pipeline_id: null }); stageForm.resetFields(); }} footer={null} width={480}>
        <Form form={stageForm} layout="vertical" onFinish={(vals) => createStage.mutate({ ...vals, pipeline_id: stageModal.pipeline_id! } as IJobStatusStageCreatePayload)} className="mt-4">
          <Form.Item name="name" label="Stage Name" rules={[{ required: true }]}><Input placeholder="e.g. In Progress" /></Form.Item>
          <Form.Item name="colour" label="Colour" initialValue={SEMANTIC_HEX.neutral}><ColorPicker format="hex" /></Form.Item>
          <Form.Item name="sort_order" label="Sort Order" initialValue={0}><Input type="number" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="is_start" label="Start Stage" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
            <Form.Item name="is_complete" label="Complete Stage" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
            <Form.Item name="is_cancelled" label="Cancelled Stage" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
            <Form.Item name="triggers_invoice" label="Triggers Invoice" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setStageModal({ open: false, pipeline_id: null }); stageForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createStage.isPending}>Add Stage</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Checklist Templates Tab ────────────────────────────────────────────────
function ChecklistsTab() {
  const [editing, setEditing] = useState<IChecklistTemplate | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemsModal, setItemsModal] = useState<IChecklistTemplate | null>(null);
  const [itemsList, setItemsList] = useState<Partial<IChecklistItem>[]>([]);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useChecklistList({ page: 1, limit: 20 });
  const items: IChecklistTemplate[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const { data: jobTypesData } = useJobTypeList({ page: 1, limit: 100 });
  const jobTypes: IJobType[] = (jobTypesData as any)?.data ?? [];
  const { data: checklistDetailData } = useChecklistDetail(itemsModal?.id || 0);
  const createMut = useChecklistCreate({ onSuccess: () => { msgApi.success("Checklist created"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); } });
  const updateMut = useChecklistUpdate({ onSuccess: () => { msgApi.success("Checklist updated"); setModalOpen(false); refetchWithQuery({ page: 1 }); } });
  const deleteMut = useChecklistDelete({ onSuccess: () => { msgApi.success("Checklist deleted"); refetchWithQuery({ page: 1 }); } });
  const saveItemsMut = useSaveChecklistItems({ onSuccess: () => { msgApi.success("Items saved"); setItemsModal(null); } });

  const openItemsModal = (template: IChecklistTemplate) => {
    setItemsModal(template);
    setItemsList((checklistDetailData as any)?.data?.items || template.items || []);
  };

  const onFinish = (vals: any) => {
    if (editing) { updateMut.mutate({ ...vals, id: editing.id } as IChecklistUpdatePayload); }
    else { createMut.mutate(vals as IChecklistCreatePayload); }
  };

  const columns: ColumnType<IChecklistTemplate>[] = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "Job Type", dataIndex: "job_type_name", key: "job_type_name", render: (v) => v ? <Tag color="purple">{v}</Tag> : <span className="text-slate-400">All types</span> },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    { title: "Created", dataIndex: "created_at", key: "created_at", render: (v) => dayjs(v).format("DD MMM YYYY") },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openItemsModal(r)}>Edit Items</Button>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete checklist?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Checklist Templates ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }} className="flex items-center gap-2">Add Checklist</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />

      <Modal title={editing ? "Edit Checklist" : "New Checklist Template"} open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={480}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="job_type_id" label="Job Type (optional)">
            <Select allowClear options={jobTypes.map((j) => ({ value: j.id, label: j.name }))} />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>{editing ? "Save" : "Create"}</Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Checklist Items — ${itemsModal?.name}`}
        open={!!itemsModal}
        onCancel={() => setItemsModal(null)}
        width={640}
        footer={
          <div className="flex justify-between">
            <Button icon={<Plus size={13} />} onClick={() => setItemsList((prev) => [...prev, { item_text: "", response_type: "checkbox", is_required: true, fail_triggers_alert: false, sort_order: prev.length }])}>
              Add Item
            </Button>
            <Space>
              <Button onClick={() => setItemsModal(null)}>Cancel</Button>
              <Button type="primary" loading={saveItemsMut.isPending} onClick={() => saveItemsMut.mutate({ template_id: itemsModal!.id, items: itemsList.filter((i) => i.item_text) as any })}>
                Save Items
              </Button>
            </Space>
          </div>
        }
      >
        <div className="space-y-2 max-h-[60vh] overflow-y-auto py-2">
          {itemsList.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200">
              <GripVertical size={14} className="text-slate-400 flex-shrink-0" />
              <Input
                className="flex-1"
                placeholder="Item text…"
                value={item.item_text || ""}
                onChange={(e) => setItemsList((prev) => prev.map((it, i) => i === idx ? { ...it, item_text: e.target.value } : it))}
              />
              <Select
                size="small"
                value={item.response_type || "checkbox"}
                onChange={(v) => setItemsList((prev) => prev.map((it, i) => i === idx ? { ...it, response_type: v } : it))}
                options={["checkbox", "pass_fail", "text", "number", "photo", "signature"].map((v) => ({ value: v, label: v }))}
                className="w-32"
              />
              <Switch size="small" checked={item.is_required} onChange={(v) => setItemsList((prev) => prev.map((it, i) => i === idx ? { ...it, is_required: v } : it))} title="Required" />
              <Switch size="small" checked={item.fail_triggers_alert} onChange={(v) => setItemsList((prev) => prev.map((it, i) => i === idx ? { ...it, fail_triggers_alert: v } : it))} title="Alert on fail" />
              <button className="text-slate-400 hover:text-red-500" onClick={() => setItemsList((prev) => prev.filter((_, i) => i !== idx))}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {itemsList.length === 0 && <p className="text-slate-400 text-center py-4">No items yet — click Add Item</p>}
        </div>
      </Modal>
    </>
  );
}

// ── Form Fields Builder ────────────────────────────────────────────────────
type FormFieldType = "text" | "textarea" | "checkbox" | "radio" | "select" | "number" | "date" | "signature" | "photo";

interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  options: string[];
}

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio (single choice)" },
  { value: "select", label: "Dropdown" },
  { value: "photo", label: "Photo" },
  { value: "signature", label: "Signature" },
];

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

function FieldRow({
  field,
  index,
  total,
  onChange,
  onDelete,
  onMove,
}: {
  field: FormField;
  index: number;
  total: number;
  onChange: (id: string, updates: Partial<FormField>) => void;
  onDelete: (id: string) => void;
  onMove: (from: number, to: number) => void;
}) {
  const hasOptions = ["radio", "select"].includes(field.type);
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 mb-2">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 pt-1">
          <button
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
          >
            <GripVertical size={14} />
          </button>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-2">
          <Input
            placeholder="Field label…"
            value={field.label}
            onChange={(e) => onChange(field.id, { label: e.target.value })}
            className="col-span-2"
          />
          <Select
            value={field.type}
            onChange={(v) => onChange(field.id, { type: v as FormFieldType, options: [] })}
            options={FIELD_TYPES}
            size="middle"
          />
        </div>
        <div className="flex items-center gap-1.5 pt-1">
          <span className="text-xs text-slate-500">Required</span>
          <Switch
            size="small"
            checked={field.required}
            onChange={(v) => onChange(field.id, { required: v })}
          />
          <button
            onClick={() => onDelete(field.id)}
            className="text-slate-400 hover:text-red-500 ml-1"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      {hasOptions && (
        <div className="mt-2 pl-7">
          <div className="text-xs text-slate-500 mb-1">Options (one per line)</div>
          <Input.TextArea
            rows={2}
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            value={field.options.join("\n")}
            onChange={(e) =>
              onChange(field.id, { options: e.target.value.split("\n").filter(Boolean) })
            }
          />
        </div>
      )}
    </div>
  );
}

function FormFieldsBuilder({
  fields,
  onChange,
}: {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
}) {
  const addField = () => {
    onChange([
      ...fields,
      { id: generateId(), type: "text", label: "", required: false, options: [] },
    ]);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const deleteField = (id: string) => {
    onChange(fields.filter((f) => f.id !== id));
  };

  const moveField = (from: number, to: number) => {
    if (to < 0 || to >= fields.length) return;
    const updated = [...fields];
    const [item] = updated.splice(from, 1);
    updated.splice(to, 0, item);
    onChange(updated);
  };

  return (
    <div>
      <div className="max-h-[50vh] overflow-y-auto pr-1">
        {fields.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            No fields yet. Click "Add Field" to get started.
          </div>
        )}
        {fields.map((f, i) => (
          <FieldRow
            key={f.id}
            field={f}
            index={i}
            total={fields.length}
            onChange={updateField}
            onDelete={deleteField}
            onMove={moveField}
          />
        ))}
      </div>
      <Button icon={<Plus size={13} />} onClick={addField} className="mt-2 w-full" type="dashed">
        Add Field
      </Button>
    </div>
  );
}

// ── Form Templates Tab ─────────────────────────────────────────────────────
function FormTemplatesTab() {
  const [editing, setEditing] = useState<IFormTemplate | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [builderFields, setBuilderFields] = useState<FormField[]>([]);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useFormTemplateList({ page: 1, limit: 20 });
  const items: IFormTemplate[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const { data: jobTypesData } = useJobTypeList({ page: 1, limit: 100 });
  const jobTypes: IJobType[] = (jobTypesData as any)?.data ?? [];

  const parseFields = (template: IFormTemplate): FormField[] => {
    try {
      const raw = typeof template.fields_schema === "string"
        ? JSON.parse(template.fields_schema)
        : template.fields_schema;
      return (raw?.fields ?? []) as FormField[];
    } catch {
      return [];
    }
  };

  const createMut = useFormTemplateCreate({ onSuccess: () => { msgApi.success("Form template created"); setModalOpen(false); form.resetFields(); setBuilderFields([]); refetchWithQuery({ page: 1 }); } });
  const updateMut = useFormTemplateUpdate({ onSuccess: () => { msgApi.success("Form template updated"); setModalOpen(false); refetchWithQuery({ page: 1 }); } });
  const deleteMut = useFormTemplateDelete({ onSuccess: () => { msgApi.success("Form template deleted"); refetchWithQuery({ page: 1 }); } });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setBuilderFields([]);
    setModalOpen(true);
  };

  const openEdit = (r: IFormTemplate) => {
    setEditing(r);
    form.setFieldsValue({ name: r.name, description: r.description, job_type_id: r.job_type_id, is_active: r.is_active });
    setBuilderFields(parseFields(r));
    setModalOpen(true);
  };

  const onFinish = (vals: any) => {
    const fields_schema = JSON.stringify({ fields: builderFields });
    if (editing) {
      updateMut.mutate({ ...vals, id: editing.id, fields_schema } as IFormTemplateUpdatePayload);
    } else {
      createMut.mutate({ ...vals, fields_schema } as IFormTemplateCreatePayload);
    }
  };

  const columns: ColumnType<IFormTemplate>[] = [
    {
      title: "Name", dataIndex: "name", key: "name",
      render: (v) => <span className="font-medium text-slate-800">{v}</span>,
    },
    { title: "Job Type", dataIndex: "job_type_name", key: "job_type_name", render: (v) => v ? <Tag color="purple">{v}</Tag> : <span className="text-slate-400">All types</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => v || "—" },
    {
      title: "Fields", key: "fields",
      render: (_, r) => {
        const count = parseFields(r).length;
        return <Tag color="blue">{count} field{count !== 1 ? "s" : ""}</Tag>;
      },
    },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    { title: "Created", dataIndex: "created_at", key: "created_at", render: (v) => dayjs(v).format("DD MMM YYYY") },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete form template?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Form Templates ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={openCreate} className="flex items-center gap-2">
          Add Form Template
        </ButtonWrapper>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />

      {/* Create/Edit Modal with fields builder */}
      <Modal
        title={editing ? "Edit Form Template" : "New Form Template"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setBuilderFields([]); }}
        footer={null}
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Name" rules={[{ required: true }]} className="col-span-2">
              <Input />
            </Form.Item>
            <Form.Item name="job_type_id" label="Job Type (optional)" className="col-span-1">
              <Select allowClear options={jobTypes.map((j) => ({ value: j.id, label: j.name }))} />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true} className="col-span-1">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Form Fields</span>
              <Button
                size="small"
                type="link"
                onClick={() => setPreviewOpen(true)}
                disabled={builderFields.length === 0}
              >
                Preview
              </Button>
            </div>
            <FormFieldsBuilder fields={builderFields} onChange={setBuilderFields} />
          </div>

          <div className="flex justify-end gap-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); setBuilderFields([]); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>
              {editing ? "Save Changes" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Form Preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={<Button onClick={() => setPreviewOpen(false)}>Close</Button>}
        width={500}
      >
        <div className="space-y-4 py-2">
          {builderFields.map((f) => (
            <div key={f.id}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {f.label || <span className="italic text-slate-400">Untitled field</span>}
                {f.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {f.type === "text" && <Input disabled placeholder="Short text…" />}
              {f.type === "textarea" && <Input.TextArea disabled rows={2} placeholder="Long text…" />}
              {f.type === "number" && <Input disabled type="number" placeholder="0" />}
              {f.type === "date" && <Input disabled placeholder="DD/MM/YYYY" />}
              {f.type === "checkbox" && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" disabled />
                  <span className="text-sm text-slate-500">{f.label || "Checkbox"}</span>
                </div>
              )}
              {(f.type === "radio" || f.type === "select") && (
                <div className="space-y-1">
                  {(f.options.length > 0 ? f.options : ["Option 1", "Option 2"]).map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type={f.type === "radio" ? "radio" : "checkbox"} disabled />
                      <span className="text-sm text-slate-600">{opt}</span>
                    </div>
                  ))}
                </div>
              )}
              {f.type === "photo" && (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center text-slate-400 text-sm">
                  📷 Photo capture
                </div>
              )}
              {f.type === "signature" && (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center text-slate-400 text-sm">
                  ✍️ Signature pad
                </div>
              )}
            </div>
          ))}
          {builderFields.length === 0 && (
            <div className="text-center py-8 text-slate-400">No fields to preview</div>
          )}
        </div>
      </Modal>
    </>
  );
}

const JobConfigPage = () => {
  const tabItems = [
    { key: "job-types", label: <span className="flex items-center gap-1.5"><Settings size={13} />Job Types</span>, children: <JobTypesTab /> },
    { key: "pipelines", label: <span className="flex items-center gap-1.5"><GitBranch size={13} />Pipelines & Stages</span>, children: <PipelinesTab /> },
    { key: "checklists", label: <span className="flex items-center gap-1.5"><CheckSquare size={13} />Checklists</span>, children: <ChecklistsTab /> },
    { key: "forms", label: <span className="flex items-center gap-1.5"><FileText size={13} />Form Templates</span>, children: <FormTemplatesTab /> },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800">Job Configuration</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage job types, pipelines, checklists and form templates</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <Tabs defaultActiveKey="job-types" items={tabItems} />
      </div>
    </div>
  );
};

export default JobConfigPage;
